import type { Feature, Point } from "@turf/helpers";
import {
  SPIDER_FACILITY_LAYER,
  SPIDER_FACILITY_SOURCE
} from "constants/mapLayers.constants";
import type { PointLike } from "mapbox-gl";

/**
 * IDs for all spider-related layers and sources that are managed
 * as a group. Order matters: layers must be removed before their
 * corresponding sources.
 */
const SPIDER_LAYER_AND_SOURCE_IDS = [
  "spider-origin-layer",
  "spider-origin-source",
  SPIDER_FACILITY_LAYER,
  SPIDER_FACILITY_SOURCE,
  "spider-legs-layer",
  "spider-legs-source"
] as const;

/**
 * Spread overlapping point features out in a circle using pixel-space offsets
 * so the radius stays visually consistent at any zoom level.
 *
 * This is a pure transformation: the only reason a map reference is required
 * is for coordinate-to-pixel projection (map.project / map.unproject).
 *
 * @param features - The clustered point features to fan out
 * @param origin   - The shared [lng, lat] coordinate all features overlap at
 * @param map      - Mapbox map instance used solely for coordinate projection
 * @param offsetPx - Pixel radius of the spider circle (default 50)
 * @returns A new array of features with their geometries repositioned
 */
export function spiderifyFeatures(
  features: readonly Feature<Point>[],
  origin: readonly [number, number],
  map: mapboxgl.Map,
  offsetPx = 50
): Feature<Point>[] {
  const centerPx = map.project({ lng: origin[0], lat: origin[1] });
  const angleStep = (2 * Math.PI) / features.length;

  return features.map((feat, i) => {
    const angle = i * angleStep;
    const x = centerPx.x + offsetPx * Math.cos(angle);
    const y = centerPx.y + offsetPx * Math.sin(angle);
    const { lng, lat } = map.unproject({ x, y } as PointLike);

    return {
      ...feat,
      geometry: {
        type: "Point" as const,
        coordinates: [lng, lat]
      },
      properties: feat.properties
    };
  });
}

/**
 * Remove all spider-related layers and sources from the map.
 *
 * Each identifier is checked for existence as both a layer and a source
 * before removal, so this is safe to call even when no spider visualisation
 * is currently active.
 *
 * @param map - Mapbox map instance to clean up
 */
export function removeSpiderLayers(map: mapboxgl.Map): void {
  if (!map) return;

  for (const id of SPIDER_LAYER_AND_SOURCE_IDS) {
    if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(id)) map.removeSource(id);
  }
}
